<template>
	<div class="mapwrapper" v-if="dataReady">
		<div class="mapwrapper__map">
			<div class="navbar" @click="viewList">
				<app-filter
					:filter="filters.county"
					:viewstate="viewstate[0]"
					handle="county"
					@updatefilter="updateFilter"
					@hidefilters="hideFilters"
				></app-filter>
				<app-filter
					:filter="filters.grade"
					:viewstate="viewstate[1]"
					handle="grade"
					@updatefilter="updateFilter"
					@hidefilters="hideFilters"
				></app-filter>
				<app-filter
					:filter="filters.length_category"
					:viewstate="viewstate[5]"
					handle="length_category"
					@updatefilter="updateFilter"
					@hidefilters="hideFilters"
				></app-filter>
				<app-filter
					:filter="filters.elevation_category"
					:viewstate="viewstate[2]"
					handle="elevation_category"
					@updatefilter="updateFilter"
					@hidefilters="hideFilters"
				></app-filter>
				<app-filter
					:filter="filters.bike_ride"
					:viewstate="viewstate[3]"
					handle="bike"
					@updatefilter="updateFilter"
					@hidefilters="hideFilters"
				></app-filter>
				<app-filter
					:filter="filters.new"
					:viewstate="viewstate[4]"
					:current-user="mapUser"
					handle="new"
					@updatefilter="updateFilter"
					@hidefilters="hideFilters"
				></app-filter>
				<form @submit.prevent="updateQuery" class="navbar__form navbar__form--inline-block">
					<input type="text" class="navbar__filter navbar__filter--narrow navbar__filter--search" v-model="queryString" placeholder="Søk..." >
					<button class="navbar__button navbar__button--map-search" >0</button>
				</form>
			</div>
			<div id="routemap"></div>
		</div>
		<app-routedetails
			:config="config"
			:route="activeRoute"
			:visible="detailsVisible"
			@close="hideRouteDetails"
		></app-routedetails>
		<app-routepanel
			:config="config"
			:favourites="favourites"
			:registrations="registrations"
			:routes="activeRoutes"
			:current-user="mapUser"
			@mouseover="routeListMouseover"
			@mouseout="routeListMouseout"
			@toggle-favourite="toggleFavourite"
		></app-routepanel>
		<div class="filter__background" @click="hideFilters" :class="{ 'filter__background--active': filteringShown }"></div>
		<div class="typeswitch">
			<a :class="{ 'typeswitch--active': (currentView == 'map') }" @click="viewMap">Kart</a>
			<a :class="{ 'typeswitch--active': (currentView == 'list') }" @click="viewList">Liste</a>
		</div>
	</div>
</template>

<script>
	import Mapfilter from './Mapfilter.vue'
	import Routepanel from './Routepanel.vue'
	import Routedetails from './Routedetails.vue';
	import 'leaflet';
	import 'leaflet.markercluster';
	import 'leaflet.locatecontrol';
	import 'leaflet-tooltip';
	import distance from '@turf/distance';
	import QueryString from 'query-string';

	export default {
		components: {
			'app-routedetails': Routedetails,
			'app-filter': Mapfilter,
			'app-routepanel': Routepanel
		},
		data() {
			return {
				municipalities: [],
				viewstate: require('../config/viewstate.json'),
				filters: require('../config/filters.json'),
				config: require('../config/config.json'),
				initialView: {
					lat: 70.318449,
					lon: 25.345568,
					// zoom: 8
					zoom: 11
				},
				viewBeforeClick: null,
				filteringShown: false, // whether filters are currently shown
				filteringActive: false,
				prevClusterTooltip: false,
				fitBoundsPending: false,
				geoFilter: [],
				currentView: 'map',
				queryString: '',
				dataReady: false,
				favourites: {},
				registrations: {},
				activeRoute: {},
				detailsVisible: false,
				activeRoutes: [],
				// Read once here (the one place this component tree legitimately
				// owns window.mapConfig) and threaded down as explicit props
				// through Routepanel to Routelist, so Routelist itself has no
				// hidden global dependency - see its own props for why.
				mapUser: window.mapConfig.user,
				mapCsrfToken: window.mapConfig.csrf
			};
		},
		watch: {
			viewstate: {
				handler: function(state, oldState) {
					let hasShownFilters = false;
					this.viewstate.forEach((filter) => {
						if (filter.isActive) {
							hasShownFilters = true;
						}
					});

					if (hasShownFilters) {
						this.filteringShown = true;
					}
					else {
						this.filteringShown = false;
					}
				},
				deep: true
			},
			filters: {
				handler: function(state, oldState) {
					let hasActiveFilters = false;
					this.viewstate.forEach((filter) => {
						if (hasActiveFilters) {
							return;
						}
						this.filters[filter.handle].options.forEach((option) => {
							if (option.isActive) {
								hasActiveFilters = true;
							}
						});
					});
					this.filteringActive = hasActiveFilters;
				},
				deep: true
			}
		},
		beforeCreate() {
			// console.log(this.filters);
		},
		created() {
			// Fetched here (rather than by app.js before mounting) so the
			// loading spinner routes.blade.php shows over the map/list panes
			// (#routemap-loading) stays up for exactly as long as this
			// component needs before it has anything to render - dataReady
			// gates the whole template (see <template> above) until then.
			const query = window.mapConfig.competition ? '?competition=' + window.mapConfig.competition : '';

			// Favourites come from the same xhr/frontend/users/favourites endpoint
			// FavouriteList.vue (the /min-side "Mine favoritter" list) uses,
			// rather than a second, duplicate favourites lookup - it requires a
			// logged-in person, so only call it when one is resolved
			// (mapConfig.user), matching the check the heart icon itself
			// already does.
			const favourites = window.mapConfig.user !== false
				? fetch(window.mapConfig.favouritesUrl)
					.then((response) => response.json())
					.then((data) => Object.fromEntries(data.favourites.map((favourite) => [favourite.page_id, 1])))
				: Promise.resolve({});

			Promise.all([
				fetch(window.mapConfig.routesUrl + query).then((response) => response.json()),
				fetch(window.mapConfig.registrationsUrl + query).then((response) => response.json()),
				favourites,
			]).then(async ([routes, registrations, favouritesMap]) => {
				// Not in data() - addMarkers() attaches a live Leaflet marker
				// instance to each route (route.marker), and this.map/
				// this.routeCluster below are the same kind of Leaflet-bearing
				// instance property, deliberately kept out of Vue's reactivity.
				this.mapData = routes;
				this.favourites = favouritesMap;
				this.registrations = registrations;

				this.setupFilters();
				this.dataReady = true;
				document.getElementById('routemap-loading')?.remove();

				// setupMap() needs #routemap in the DOM, which only exists
				// once the v-if above has re-rendered the template.
				await this.$nextTick();
				this.setupMap();
			});
		},
		methods: {
			// Routelist.vue only emits intent - the actual POST (and the CSRF
			// token it needs) lives here, the root of this component tree, so
			// Routelist stays a plain reusable list (the /min-side "Favoritter"
			// tab reuses it too, via its own root, FavouriteList.vue).
			toggleFavourite(route) {
				const isFavourite = !this.favourites[route.page_id];
				this.favourites[route.page_id] = isFavourite;

				fetch('/xhr/frontend/users/favourites/' + route.page_id, {
					method: 'POST',
					headers: { 'X-CSRF-TOKEN': this.mapCsrfToken },
				});
			},
			setupFilters() {
				const routes = this.mapData;
				let routeMunicipalities = {};

				routes.forEach((route) => {
					if (route.countyName) {
						routeMunicipalities[route.countyName] = 1;
					}
					route.counties = route.countyName ? [route.countyName] : [];
				});

				let counties = Object.keys(routeMunicipalities).sort((a, b) => a.localeCompare(b, 'nb'));

				counties.forEach((value, index) => {
					this.filters.county.options.push({
						id: value,
						title: value,
						isActive: false
					});
				});
			},
			setupMap() {
				const wms = 'https://cache.kartverket.no/v1/wmts/1.0.0/topo/default/webmercator/{z}/{y}/{x}.png';
				const tileLayer = L.tileLayer(wms, {
					attribution: '© <a href="http://www.kartverket.no/">Kartverket</a>',
					tileSize: 256,
				});

				this.map = L.map('routemap', {
					scrollWheelZoom: true,
					zoomControl: false,
				});
				this.map.addLayer(tileLayer);

				this.routeCluster = L.markerClusterGroup({
					chunkedLoading: true,
					maxClusterRadius: 35
				});
				L.control.zoom({ position: 'bottomright' }).addTo(this.map);
				L.control.locate({
					icon: 'location-btn',
					iconLoading: 'location-btn location-spinner',
					strings: {
						title: 'Vis min posisjon',
						popup: "Du er innenfor {distance} {unit} fra dette punktet",
					},
					locateOptions: {
						maxZoom: 14,
						enableHighAccuracy: true
					},
					position: 'bottomright'
				}).addTo(this.map);

				const killTooltip = () => {
					if (this.prevClusterTooltip) {
						this.map.removeLayer(this.prevClusterTooltip);
					}
				};

				this.routeCluster.on('clustermouseover', (c) => {
					killTooltip();
					this.prevClusterTooltip = this.getClusterTooltip(c.layer, c.layer._childCount + ' turer');
				}).on('clustermouseout', (c) => {
					killTooltip();
				}).on('clusterclick', (c) => {
					killTooltip();
				});

				this.addMarkers();
				// this.showMunicipalityMarkers();
				this.showRouteMarkers();

				let viewUpdated = this.getParamsFromUrl();

				if (!viewUpdated) {
					//this.map.setView([this.initialView.lat, this.initialView.lon], this.initialView.zoom);
					this.resetView();
				}

				this.map.on('zoomend', () => {
					this.orderByClosest();
					this.updateUrl();
					return;

					let zoom = this.map.getZoom();

					if (zoom < 10) {
						this.showMunicipalityMarkers();
						this.hideRouteMarkers();
					}
					else {
						this.hideMunicipalityMarkers();
						this.showRouteMarkers();
					}
				});

				this.map.on('dragend', () => {
					this.orderByClosest();
					this.updateUrl();
				});
			},
			addMarkers() {
				this.routeMarkers = [];
				this.municipalityMarkers = [];

				// bounding box for norway (TODO: change to MR)
				const maxLat = 80.6571442736;
				const minLat = 58.0788841824;
				const minLon = 4.99207807783;
				const maxLon = 31.29341841;

				for (let municipality in this.municipalities) {
					if (this.municipalities.hasOwnProperty(municipality)) {
						this.municipalityMarkers.push(this.createMunicipalityMarker(municipality));
					}
				}

				let mapData = this.mapData;
				let mapMarkers = {
					'0_x': "icons/map_icon_gray.svg",
					'0_0': "icons/map_icon_green.svg",
					'0_1': "icons/map_icon_blue.svg",
					'0_2': "icons/map_icon_red.svg",
					'0_3': "icons/map_icon_black.svg",
					'1_x': "icons/map_icon_ski_gray.svg",
					'1_0': "icons/map_icon_ski_green.svg",
					'1_1': "icons/map_icon_ski_blue.svg",
					'1_2': "icons/map_icon_ski_red.svg",
					'1_3': "icons/map_icon_ski_black.svg",
					'2_x': "icons/map_icon_bike_gray.svg",
					'2_0': "icons/map_icon_bike_green.svg",
					'2_1': "icons/map_icon_bike_blue.svg",
					'2_2': "icons/map_icon_bike_red.svg",
					'2_3': "icons/map_icon_bike_black.svg",
					'3_x': "icons/map_icon_kayak_gray.svg",
					'3_0': "icons/map_icon_kayak_green.svg",
					'3_1': "icons/map_icon_kayak_blue.svg",
					'3_2': "icons/map_icon_kayak_red.svg",
					'3_3': "icons/map_icon_kayak_black.svg",
				};

				let routeIcons = {};

				let iconSize = [20,30];
				let iconAnchor = [10,30];

				for (let key in mapMarkers) {
					if (mapMarkers.hasOwnProperty(key)) {
						routeIcons[key] = L.icon({
							iconUrl: '/' + mapMarkers[key],
							iconSize: iconSize,
							iconAnchor: iconAnchor,
							shadowSize: [0,0],
							popupAnchor:  [0, -30]
						});
					}
				}

				let markerConfig = {
					pane: 'popupPane',
					offset: [0, -20],
					direction: 'auto'
				};

				mapData.forEach((route, i) => {

					let iconKey = '';
					if(route.opening_date){ // closed route
						iconKey = route.type + '_x';
					} else {
						iconKey = route.type + '_' + route.grade;
					}

					if (!mapMarkers[iconKey] || route.lon < minLon || route.lon > maxLon || route.lat < minLat || route.lat > maxLat) {
						return;
					}

					route.marker = L.marker([route.lat, route.lon], {icon: routeIcons[iconKey]});
					route.marker.bindTooltip(route.name, markerConfig);
					route.marker.index = i;
					route.marker.on('click', (e) => {
						this.showRouteDetails(route);
					});
					route.visible = true;

					this.routeMarkers.push(route.marker);
				});

				this.mapData = mapData;
				this.map.addLayer(this.routeCluster);
				this.updateMarkerList();
			},
			isSwitcherVisible() {
				const switchStyles = window.getComputedStyle(document.querySelector('.typeswitch'));
				return (switchStyles.display !== 'none');
			},
			showRouteDetails(route, initial) {
				this.activeRoute = route;
				this.detailsVisible = true;
				let center = this.map.getCenter();
				if (!initial) {
					this.viewBeforeClick = {
						latlon: center,
						zoom: this.map.getZoom(),
						route: route.page_id
					};
				}

				if (this.currentView === 'list' && this.isSwitcherVisible()) {
					document.querySelector('.routepanel').classList.remove('routepanel--active');
				}
				// let currentZoom = this.map.getZoom();
				// let selectedTripZoom = 13;
				// let newZoom = currentZoom < selectedTripZoom ? selectedTripZoom : currentZoom;

				// TODO: use bounds for start / end point
				// this.map.setView([route.lat, route.lon], newZoom);

				if (this.prevTrack) {
					this.map.removeLayer(this.prevTrack);
				}
				if (this.startMarker) {
					this.map.removeLayer(this.startMarker);
				}

				let hasLoadedTrack = false;

				setTimeout(() => {
					if (!hasLoadedTrack) {
						this.map.setView(route.marker.getLatLng());
					}
				}, 1000);

				// load json
				let r = new XMLHttpRequest();
				r.open("GET", "/xhr/frontend/routes/"+route.page_id+"/gpx", true);
				r.onreadystatechange = () => {
					if (r.readyState != 4 || r.status != 200 || !r.responseText) {
						return;
					}

					let routeData = JSON.parse(r.responseText);
					if (!routeData || !routeData.track) {
						return;
					}
					let routepoints = routeData.track;
					if (routepoints.length === 0) {
						return;
					}

					hasLoadedTrack = true;

					// let markerText = 'Start tur til ' + route.name;
					// let labelOffset = [20,-30];

					// this.startMarker = L.marker([routepoints[0][1], routepoints[0][0]], { icon: icons.start })
					// 	.bindPopup('<strong>' + markerText + '</strong>')
					// 	.addTo(this.map)
					// 	.bindLabel(markerText, {
					// 		pane: 'popupPane',
					// 		offset: labelOffset,
					// 		direction: 'auto'
					// 	});

					let geojson = {"name":"NewFeatureType","type":"FeatureCollection" ,"features":[{"type":"Feature","geometry":{"type":"LineString","coordinates":routepoints},"properties":null}]};
					let gjl = L.geoJson(geojson, {});
					let bounds = gjl.getBounds();
					bounds.extend([route.lat, route.lon]);

					this.prevTrack = gjl;
					this.map.once('moveend', () => {
						gjl.addTo(this.map)
					});
					this.fitBounds(bounds, {
						paddingTopLeft: L.point(0, 50),
						paddingBottomRight: L.point(0, 200)
					});
				};
				r.send();
			},
			hideRouteDetails(preventMapmove) {
				this.detailsVisible = false;
				if (this.currentView === 'list' && this.isSwitcherVisible()) {
					document.querySelector('.routepanel').classList.add('routepanel--active');
				}
				if (!preventMapmove && this.viewBeforeClick) {
					if (this.map.getZoom() > this.viewBeforeClick.zoom) {
						this.map.setView(this.viewBeforeClick.latlon, this.viewBeforeClick.zoom);
					}
					this.viewBeforeClick = null;
				}
				if (this.prevTrack) {
					this.map.removeLayer(this.prevTrack);
					this.prevTrack = false;
				}
			},
			createMunicipalityMarker(name) {
				let municipality = this.municipalities[name];
				let width = name.length*11;
				if (width < 60) {
					width = 60;
				}
				let areaIcon = L.divIcon({
					className: 'areaBtn',
					html: '<span>' + name + '</span>',
					iconSize: [width, 24]
				});
				let marker = L.marker([municipality['center'][1], municipality['center'][0]], {
					icon: areaIcon,
					// draggable: true
				});
				// marker.on('dragend', (e) => {
				// 	var pos = this.getLatLng();
				// 	municipality = [pos.lng, pos.lat];
				// });
				marker.on('click', (e) => {
					var box = municipality['bound'];
					this.map.fitBounds([
						[box.maxy, box.maxx],
						[box.miny, box.minx]
					]);
				});
				return marker;
			},
			showMunicipalityMarkers() {
				for (let i=0; i<this.municipalityMarkers.length; i++) {
					this.municipalityMarkers[i].addTo(this.map);
				}
			},
			hideMunicipalityMarkers() {
				for (let i=0; i<this.municipalityMarkers.length; i++) {
					this.map.removeLayer(this.municipalityMarkers[i]);
				}
			},
			showRouteMarkers() {
				for (let i=0; i<this.routeMarkers.length; i++) {
					this.routeCluster.addLayer(this.routeMarkers[i]);
				}
			},
			hideRouteMarkers() {
				for (let i=0; i<this.routeMarkers.length; i++) {
					this.routeCluster.removeLayer(this.routeMarkers[i]);
				}
			},
			hideFilters() {
				for (let i=0; i<this.viewstate.length; i++) {
					this.viewstate[i].isActive = false;
				}
			},
			updateQuery() {
				this.updateFilters(false);
				this.updateMarkerList();
			},
			updateFilter(handle) {
				this.updateFilters();
			},
			updateFilters(preventFitBounds) {
				let filters = {};
				let hasActive = false;
				let geoFilter = [];
				this.viewstate.forEach((el) => {
					filters[el.handle] = [];
					this.filters[el.handle].options.forEach((option) => {
						if (option.isActive) {
							filters[el.handle].push(option.id);
							hasActive = true;
							if (el.handle === 'county') {
								geoFilter.push(option.id);
							}
						}
					});
				});
				/*
				if (JSON.stringify(geoFilter) !== JSON.stringify(this.geoFilter)) {
					this.geoFilter = geoFilter;
					if (geoFilter.length && !preventFitBounds) {
						this.fitBoundsPending = true;
					}
				}
				else {
					// console.log('equal');
				}
				*/

				if (this.queryString) {
					hasActive = true;
				}

				if (!preventFitBounds) {
					this.hideRouteDetails(true);
				}

				if (!hasActive) {
					this.mapData.forEach((route) => {
						if (!route.marker) {
							return;
						}
						if (!route.visible) {
							this.routeCluster.addLayer(route.marker);
							route.visible = true;
						}
					});
					this.updateMarkerList();
					if (!preventFitBounds) {
						this.resetView();
					}
					return;
				}
				else {
					this.fitBoundsPending = true;
				}

				let bounds = L.latLngBounds([]);
				let nVisible = 0;

				this.mapData.forEach((route) => {
					if (!route.marker) {
						return;
					}
					let visible = (
						(!this.queryString || route.name.toLowerCase().indexOf(this.queryString.toLowerCase()) !== -1) &&
						(!filters.county.length || route.counties.filter(c => filters.county.includes(c)).length) &&
						(!filters.grade.length || filters.grade.includes(String(route.grade))) &&
						(!filters.bike_ride.length || filters.bike_ride.includes(route.bike_ride)) &&
						(!filters.new.length || filters.new.some((id) => this.matchesOtherFilter(id, route))) &&
						(!filters.elevation_category.length || filters.elevation_category.includes(route.elevation_category)) &&
						(!filters.length_category.length || filters.length_category.includes(route.length_category))
					);
					if (visible) {
						bounds.extend([route.lat, route.lon]);
						nVisible++;
						if (route.visible) {
							return;
						}
						this.routeCluster.addLayer(route.marker);
					}
					else {
						if (!route.visible) {
							return;
						}
						this.routeCluster.removeLayer(route.marker);
					}
					route.visible = visible;
				});
				if (nVisible > 0 && !preventFitBounds) {
					// TODO: make smarter
					if (this.fitBoundsPending) {
						this.fitBounds(bounds);
						this.fitBoundsPending = false;
					}
					this.updateUrl();
				}
				this.updateMarkerList();
			},
			getSidebarWidth() {
				// TODO: handle mobile, check if active etc.
				return 450;
			},
			resetView() {
				let bounds = this.getMarkerBounds();
				this.fitBounds(bounds);
			},
			getMarkerBounds() {
				let bounds = L.latLngBounds([]);
				this.mapData.forEach((route) => {
					if (!route.marker || !route.lat || !route.lon) {
						return;
					}
					bounds.extend([route.lat, route.lon]);
				});
				return bounds;
			},
			fitBounds(bounds, configs) {
				let config = {};
				if (configs) {
					config = configs;
				}
				else {
					config = {
						paddingTopLeft: L.point(50, 50),
						//maxZoom: newZoom
						// paddingBottomRight: L.point(this.getSidebarWidth(), 0)
					}
				}
				const labels = document.querySelectorAll('.leaflet-pane .tooltip');
				for (let i=0; i<labels.length; i++) {
					labels[i].style.display = 'none';
				}
				this.map.fitBounds(bounds, config);
			},
			getMapBounds() {
				const sidebarWidth = this.getSidebarWidth();
				let bounds = null;

				if (sidebarWidth > 0) {
					// we need take sidebar width into account, get existing bounds for the map
					const oldBounds = this.map.getBounds();
					const southEast = oldBounds.getSouthEast();

					// get lng value of sidebar left edge
					southEast.lng = this.map.layerPointToLatLng([this.map.getSize().x - sidebarWidth, 0]).lng;

					// create new bounds with sidebar width subtracted
					return new L.latLngBounds(oldBounds.getNorthWest(), southEast);
				}
				else {
					return this.map.getBounds();
				}
			},
			updateMarkerList() {
				const mapData = this.mapData;
				const activeRoutes = [];

				mapData.forEach((route) => {
					if (route.visible) {
						activeRoutes.push(route);
					}
				});

				this.activeRoutes = activeRoutes;
			},
			// The "Andre filter" group mixes route.new (a plain field, like
			// every other filter) with two login-only options that aren't
			// fields on the route at all - whether it's in the current
			// visitor's own favourites/registrations maps (this.favourites/
			// this.registrations, keyed by page_id, already loaded for the
			// heart/checkmark badges elsewhere on this page).
			matchesOtherFilter(id, route) {
				if (id === 'favourites') {
					return !!this.favourites[route.page_id];
				}
				if (id === 'registered') {
					return !!this.registrations[route.page_id];
				}
				return route.new === id;
			},
			getClusterTooltip(cluster, text) {
				let latLng = cluster.getLatLng();
				let tooltipPosition = (latLng.lng > this.map.getCenter().lng) ? 'left' : 'right';
				return L.tooltip({
						position: tooltipPosition,
						wrapScreen: false
					})
					.addTo(this.map)
					.setLatLng(latLng)
					.setContent(text);
			},
			routeListMouseover(route) {
				let marker = this.routeCluster.getVisibleParent(route.marker);
				if (marker instanceof L.MarkerCluster) {
					route.clusterTooltip = this.getClusterTooltip(marker, route.name);
				}
				else if (marker instanceof L.Marker) {
					route.marker.fire('mouseover');
				}
			},
			routeListMouseout(route) {
				if (route.clusterTooltip) {
					this.map.removeLayer(route.clusterTooltip);
				}
				else {
					route.marker.fire('mouseout');
				}
			},
			setUserMunicipalityPosition() {
				if (!window.mapConfig.user_municipality) {
					return false;
				}
				let userPos = window.mapConfig.user_municipality;
				let selected = false;
				Object.keys(this.municipalities).forEach(name => {
					if (name == userPos) {
						selected = this.municipalities[name];
					}
				});
				if (!selected) {
					return false;
				}
				this.orderByClosest(selected.center[1], selected.center[0]);
				this.fitBounds(L.latLngBounds([
					[selected.bound.miny, selected.bound.minx],
					[selected.bound.maxy, selected.bound.maxx]
				]));
				return true;
			},
			orderByClosest(lat, lon) {
				if (!lat || !lon) {
					const center = this.map.getCenter();
					lat = center.lat;
					lon = center.lng;
				}
				let comps = 0;
				const distances = {};

				this.mapData.forEach((route) => {
					// Euclidean distance should be good enough here
					var a = lat - route.lat;
					var b = lon - route.lon;
					distances[route.page_id] = Math.sqrt(a*a + b*b);
				});
				this.mapData.sort((a, b) => {
					return distances[a.page_id] - distances[b.page_id];
				});
				this.updateMarkerList();
			},
			orderByClosestMunicipality(lat, lon) {
				if (!lat || !lon) {
					const center = this.map.getCenter();
					lat = center.lat;
					lon = center.lng;
				}
				const from = [lat, lon];
				let shortestDistance = false;
				let closest = false;

				Object.keys(this.municipalities).forEach(name => {
					const target = this.municipalities[name];
					const to = [target.center[1], target.center[0]];
					const dist = distance(from, to, { units: 'kilometers' });
					if (!shortestDistance || dist < shortestDistance) {
						shortestDistance = dist;
						closest = target;
					}
				});

				if (closest) {
					this.orderByMunicipalities(closest);
				}
			},
			orderByMunicipalities(municipality) {
				const from = [municipality.center[1], municipality.center[0]];
				const municipalityMap = {};
				Object.keys(this.municipalities).forEach(name => {
					const target = this.municipalities[name];
					const to = [target.center[1], target.center[0]];
					const dist = distance(from, to, { units: 'kilometers' });
					target.distance = dist;
					municipalityMap[this.municipalities[name].id] = target;
				});
				this.mapData.sort((a, b) => {
					const from = municipalityMap[a.county];
					const to = municipalityMap[b.county];

					if (!from || !to || from.distance == to.distance) {
						return ('' + a.name).localeCompare(b.name);
					}
					return from.distance < to.distance ? -1 : 1;
				});
				this.updateMarkerList();
			},
			getParamsFromUrl() {
				if (!location.search) {
					return this.setUserMunicipalityPosition();
				}

				const params = QueryString.parse(location.search);
				let viewUpdated = false;

				this.viewstate.forEach((filter) => {
					if (typeof params[filter.handle] !== typeof undefined) {
						let options = this.filters[filter.handle].options;
						let param = params[filter.handle];
						if (!Array.isArray(param)) {
							param = [param];
						}

						this.filters[filter.handle].options.forEach((option, index) => {
							if (param.includes(option.id)) {
								option.isActive = true;
								if (filter.handle === 'county') {
									this.geoFilter.push(option.id);
								}
							}
						});
					}
				});

				if (params.lat && params.lng && params.zoom) {
					this.map.setView([params.lat, params.lng], params.zoom);
					viewUpdated = true;
				}
				else {
					viewUpdated = this.setUserMunicipalityPosition();
				}
				if (params.q) {
					this.queryString = params.q;
					viewUpdated = true;
				}
				if (params.view === 'list') {
					this.viewList();
				}
				if (params.px && params.py && params.pz && params.r) {
					this.viewBeforeClick = {
						route: params.r,
						zoom: parseInt(params.pz),
						latlon: {
							lat: params.py,
							lng: params.px
						}
					};
					this.mapData.forEach((route, i) => {
						if (route.page_id == params.r) {
							this.showRouteDetails(route, true);
						}
					});
				}
				if (viewUpdated) {
					this.updateFilters(true);
				}

				return viewUpdated;
			},
			roundCoordinate(number) {
				// rounding decimals, from https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Math/round
				let precision = 8;
				var shift = function (number, precision, reverseShift) {
					if (reverseShift) {
						precision = -precision;
					}
					var numArray = ("" + number).split("e");
					return +(numArray[0] + "e" + (numArray[1] ? (+numArray[1] + precision) : precision));
				};
				return shift(Math.round(shift(number, precision, false)), precision, true);
			},
			updateUrl() {
				const pos = this.map.getCenter();
				const zoom = this.map.getZoom();

				const params = {
					lat: this.roundCoordinate(pos.lat),
					lng: this.roundCoordinate(pos.lng),
					zoom: zoom
				}

				this.viewstate.forEach((filter) => {
					this.filters[filter.handle].options.forEach((option, index) => {
						if (option.isActive) {
							if (Array.isArray(params[filter.handle])) {
								params[filter.handle].push(option.id);
							}
							else if (params[filter.handle]) {
								params[filter.handle] = [params[filter.handle], option.id];
							}
							else {
								params[filter.handle] = option.id;
							}
						}
					});
				});

				if (this.viewBeforeClick) {
					params.r = this.viewBeforeClick.route;
					params.py = this.roundCoordinate(this.viewBeforeClick.latlon.lat);
					params.px = this.roundCoordinate(this.viewBeforeClick.latlon.lng);
					params.pz = this.viewBeforeClick.zoom;
				}

				if (this.queryString) {
					params.q = this.queryString;
				}

				if (this.currentView !== 'map') {
					params.view = this.currentView;
				}

				const query = QueryString.stringify(params, {
					arrayFormat: 'bracket'
				});

				history.replaceState({}, false, '?' + query);
			},
			viewMap() {
				this.currentView = 'map';
				document.querySelector('.routepanel').classList.remove('routepanel--active');
				this.updateUrl();
			},
			viewList() {
				this.currentView = 'list';
				document.querySelector('.routepanel').classList.add('routepanel--active');
				this.updateUrl();
			}
		}
	}
</script>

<style>

</style>